import { createSelector, createSlice, type PayloadAction } from "@reduxjs/toolkit"
import { type ProductState, type ProductType } from "./types"
import { type RootState } from "store/store"
import { type HomeCategoryKey } from "services/adminApi"
import { isSelectableMakeupProductType } from "./makeupProductTypes"

const createInitialState = (): ProductState => ({
  // Per-merchant categories arrive from GET /admin/home (seeded by
  // AuthorizedUserLayout); starting empty+loading keeps the sidebar and the
  // category route guard from flashing categories the merchant never chose.
  selectedCategories: [],
  productsCategoriesLoading: true,
  product: null,
  types: [],
})

const initialState: ProductState = createInitialState()

export const productsSlice = createSlice({
  name: "currentProduct",
  initialState,
  reducers: {
    setSelectedCategories: (state, action: PayloadAction<HomeCategoryKey[]>) => {
      state.selectedCategories = [...new Set(action.payload)]
    },
    // Optimistic path for the Home category select: the category must be in
    // the sidebar/guard state BEFORE navigation to the add-product flow.
    addSelectedCategory: (state, action: PayloadAction<HomeCategoryKey>) => {
      if (!state.selectedCategories.includes(action.payload))
        state.selectedCategories.push(action.payload)
    },
    setProductsCategoriesLoading: (state, action: PayloadAction<boolean>) => {
      state.productsCategoriesLoading = action.payload
    },
    setCurrentProduct: (state, action: PayloadAction<any>) => {
      state.product = action.payload
    },
    clearProductSlice: () => {
      return createInitialState()
    },
    setTypes: (state, action: PayloadAction<ProductType[]>) => {
      state.types = action.payload
    },
  },
})

export const {
  setCurrentProduct,
  clearProductSlice,
  setSelectedCategories,
  addSelectedCategory,
  setProductsCategoriesLoading,
  setTypes,
} = productsSlice.actions

export const selectSelectedCategories = (state: RootState) => state.products.selectedCategories

export const selectCategoriesLoading = (state: RootState) =>
  state.products.productsCategoriesLoading

/**
 * Only what a merchant may PICK. The unfiltered list stays in
 * `state.products.types` because an existing product still has to resolve its
 * stored type name to an id — filtering that would send `typeId: null` on every
 * save and 400 the request.
 */
export const selectTypes = createSelector([(state: RootState) => state.products.types], (types) =>
  types.filter((type) => isSelectableMakeupProductType(type.name))
)

export default productsSlice.reducer
